/* Mimartis theme — core. Defines window.Mim; other module files self-register
   against it. Concatenated first into the global mim-theme.js bundle. */

(function () {
  'use strict';

  const Mim = {
    version: '0.0.1',
    config: {
      prefix: 'mim',
      dataAttr: 'mim',
    },
    utils: {
      qs(selector, root = document) {
        return root.querySelector(selector);
      },
      qsa(selector, root = document) {
        return Array.from(root.querySelectorAll(selector));
      },
      on(el, event, handler, options) {
        el.addEventListener(event, handler, options);
        return () => el.removeEventListener(event, handler, options);
      },
      debounce(fn, wait = 150) {
        let t;
        return function (...args) {
          clearTimeout(t);
          t = setTimeout(() => fn.apply(this, args), wait);
        };
      },
    },
    modules: {},
    register(name, module) {
      if (this.modules[name]) return this.modules[name]; // guard double-load
      this.modules[name] = module;
      if (typeof module.init === 'function') module.init();
      return module;
    },
    ready(fn) {
      if (document.readyState !== 'loading') fn();
      else document.addEventListener('DOMContentLoaded', fn);
    },
  };

  window.Mim = Mim;
  Mim.ready(() => document.documentElement.classList.add('mim-ready'));
})();

/* Drawer — generic [data-mim-drawer] open/close (cart, nav, search) + Esc. */

(function () {
  'use strict';
  if (!window.Mim) return;
  const Mim = window.Mim;

  const Drawer = {
    open(name) {
      const root = document.querySelector(`[data-mim-drawer="${name}"]`);
      if (!root) return;
      // One drawer at a time (e.g. adding to cart from the quick view).
      Mim.utils.qsa('[data-mim-drawer].is-open').forEach((el) => {
        if (el !== root) this.close(el.dataset.mimDrawer);
      });
      root.hidden = false;
      // Force reflow so the transition runs from the initial transform.
      root.offsetHeight;
      root.classList.add('is-open');
      document.body.classList.add('is-drawer-open');
      const panel = root.querySelector('.mim-drawer__panel');
      if (panel) panel.focus();
      this._active = root;
    },

    close(name) {
      const root = name
        ? document.querySelector(`[data-mim-drawer="${name}"]`)
        : this._active;
      if (!root) return;
      root.classList.remove('is-open');
      document.body.classList.remove('is-drawer-open');
      const onEnd = () => {
        if (root.classList.contains('is-open')) return; // re-opened meanwhile
        root.hidden = true;
        root.removeEventListener('transitionend', onEnd);
      };
      root.addEventListener('transitionend', onEnd);
      // No transitionend with animations off (0ms) — hide on a timer as well.
      const ms = parseFloat(getComputedStyle(root).getPropertyValue('--mim-anim-duration')) || 0;
      setTimeout(onEnd, ms * 1.4 + 50);
      if (this._active === root) this._active = null;
    },

    closeAll() {
      Mim.utils.qsa('[data-mim-drawer]').forEach((el) => {
        if (!el.hidden) this.close(el.dataset.mimDrawer);
      });
    },

    init() {
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-mim-drawer-open]');
        if (opener) {
          e.preventDefault();
          this.open(opener.dataset.mimDrawerOpen);
          return;
        }
        const closer = e.target.closest('[data-mim-drawer-close]');
        if (closer) {
          const root = closer.closest('[data-mim-drawer]');
          if (root) this.close(root.dataset.mimDrawer);
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeAll();
      });
    },
  };

  Mim.ready(() => Mim.register('drawer', Drawer));
})();

/* Menu — header dropdown / mega-menu (desktop) + nested drawer accordions
   (mobile). Desktop hover/focus is CSS-only; this adds click + keyboard
   (touch, Enter/Space, Escape) and outside-click dismissal. Part of the
   global bundle.
   Mega menu panels are theme blocks rendered once in the header
   ([data-mim-mega-panel="<item handle>"]); on load they are moved into the
   nav item with the same handle ([data-mim-mega-item]), after which they
   behave like any dropdown. In the theme editor, selecting a Mega menu (or
   one of its promo tiles) opens it. */

(function () {
  'use strict';
  if (!window.Mim) return;
  const Mim = window.Mim;

  const Menu = {
    init() {
      this._onClick = this._onClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      document.addEventListener('click', this._onClick);
      document.addEventListener('keydown', this._onKeydown);

      this.placeMegaPanels(document);
      document.addEventListener('shopify:section:load', (e) => this.placeMegaPanels(e.target));
      document.addEventListener('shopify:block:select', (e) => this._editorToggle(e.target, true));
      document.addEventListener('shopify:block:deselect', (e) => this._editorToggle(e.target, false));
    },

    placeMegaPanels(scope) {
      Mim.utils.qsa('[data-mim-mega-panel]', scope).forEach((panel) => {
        const key = panel.dataset.mimMegaPanel;
        const header = panel.closest('[data-mim-header]') || document;
        const item = Mim.utils.qsa('[data-mim-mega-item]', header).find((el) => el.dataset.mimMegaItem === key);
        if (!item) return; // no menu item with that title: stays parked
        // Two Mega menu blocks for one item: the first wins.
        const current = item.querySelector('[data-mim-mega-panel]');
        if (current && current !== panel) return;

        // Desktop: the panel replaces the item's dropdown; items without
        // sub-links get their (hidden) toggle back.
        Mim.utils.qsa(':scope > .mim-header__dropdown:not([data-mim-mega-panel])', item).forEach((el) => el.remove());
        this.sortMegaBlocks(panel);
        item.appendChild(panel);
        item.classList.add('mim-header__nav-item--has-children', 'mim-header__nav-item--mega');
        const toggle = item.querySelector('[data-mim-submenu-toggle]');
        if (toggle) {
          toggle.hidden = false;
          if (panel.id) toggle.setAttribute('aria-controls', panel.id);
        }
        this.buildMobile(key, panel, toggle);
      });
    },

    // Nested blocks arrive in one list (the merchant's order); menu columns
    // go to the left area, cards (promo / collection / product) to the
    // right one — a carousel track when the block uses the slider layout.
    sortMegaBlocks(panel) {
      const source = panel.querySelector('[data-mim-mega-source]');
      const menus = panel.querySelector('[data-mim-mega-menus]');
      const cards = panel.querySelector('[data-mim-mega-cards]');
      if (source && menus && cards) {
        Array.from(source.children).forEach((wrap) => {
          if (wrap.querySelector('.mim-header__mega-col')) menus.appendChild(wrap);
          else {
            if (cards.hasAttribute('data-mim-carousel-track')) wrap.classList.add('mim-carousel__slide');
            cards.appendChild(wrap);
          }
        });
        source.remove();
      }
      const grid = panel.querySelector('.mim-header__mega');
      if (!grid || !menus || !cards) return;
      const hasBlockCols = !!menus.querySelector('.mim-header__mega-col--block');
      const menuCount = hasBlockCols ? menus.querySelectorAll('.mim-header__mega-col--block').length
        : menus.querySelectorAll('.mim-header__mega-col--auto').length;
      grid.classList.toggle('has-menus', menuCount > 0);
      grid.classList.toggle('has-cards', cards.children.length > 0);
    },

    // Mobile drawer: the item's submenu is rebuilt from the panel's links
    // (column headings → child links, lists → indented links), so a separate
    // columns menu — or an item that had no sub-links — shows there too.
    buildMobile(key, panel, deskToggle) {
      const entry = Mim.utils.qsa('[data-mim-drawer-key]').find((el) => el.dataset.mimDrawerKey === key);
      if (!entry) return;
      // Column by column, in panel order: heading → child link, list links
      // → indented (or child links when the column has no heading).
      const links = [];
      Mim.utils.qsa('.mim-header__mega-col', panel).forEach((col) => {
        if (getComputedStyle(col).display === 'none') return; // auto columns replaced by blocks
        const heading = col.querySelector('.mim-header__mega-heading');
        if (heading) {
          const href = heading.getAttribute('href');
          links.push(href
            ? `<a class="mim-drawer__link mim-drawer__link--child" href="${href}">${heading.innerHTML}</a>`
            : `<span class="mim-drawer__link mim-drawer__link--child mim-drawer__link--label">${heading.innerHTML}</span>`);
        }
        Mim.utils.qsa('.mim-header__mega-list a', col).forEach((a) => {
          const level = heading ? 'grandchild' : 'child';
          links.push(`<a class="mim-drawer__link mim-drawer__link--${level}" href="${a.getAttribute('href')}">${a.innerHTML}</a>`);
        });
      });
      if (!links.length) return;

      let item = entry;
      if (entry.tagName === 'A') {
        // A plain link becomes an item with a toggle and a submenu.
        item = document.createElement('div');
        item.className = 'mim-drawer__item';
        item.setAttribute('data-mim-drawer-item', '');
        item.dataset.mimDrawerKey = key;
        const row = document.createElement('div');
        row.className = 'mim-drawer__item-row';
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'mim-drawer__submenu-toggle';
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('data-mim-drawer-submenu-toggle', '');
        if (deskToggle) {
          toggle.setAttribute('aria-label', deskToggle.getAttribute('aria-label') || '');
          toggle.innerHTML = deskToggle.innerHTML;
        }
        entry.replaceWith(item);
        entry.removeAttribute('data-mim-drawer-key');
        row.append(entry, toggle);
        const submenu = document.createElement('div');
        submenu.className = 'mim-drawer__submenu';
        submenu.setAttribute('data-mim-drawer-submenu', '');
        submenu.hidden = true;
        item.append(row, submenu);
      }
      const submenu = item.querySelector(':scope > [data-mim-drawer-submenu]');
      if (submenu) submenu.innerHTML = links.join('');
    },

    // The editor targets the block's wrapper (left behind in the holder when
    // the panel moved), so find the panel by block id as well.
    _editorToggle(target, open) {
      const id = target.dataset && target.dataset.blockId
        || (target.id || '').replace(/^shopify-block-/, '');
      const panel = (target.closest && target.closest('[data-mim-mega-panel]'))
        || (id && document.getElementById(`mim-mega-${id}`));
      const item = panel && panel.closest('[data-mim-menu-item]');
      if (!item) return;
      if (open) item.classList.add('is-open');
      else this._closeDesk(item);
    },

    _onClick(e) {
      // Desktop dropdown toggle
      const deskToggle = e.target.closest('[data-mim-submenu-toggle]');
      if (deskToggle) {
        e.preventDefault();
        const item = deskToggle.closest('[data-mim-menu-item]');
        if (!item) return;
        const open = !item.classList.contains('is-open');
        Mim.utils.qsa('[data-mim-menu-item].is-open').forEach((el) => {
          if (el !== item) this._closeDesk(el);
        });
        item.classList.toggle('is-open', open);
        deskToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        return;
      }

      // Mobile drawer accordion toggle
      const drawerToggle = e.target.closest('[data-mim-drawer-submenu-toggle]');
      if (drawerToggle) {
        e.preventDefault();
        const row = drawerToggle.closest('.mim-drawer__item-row');
        const submenu = row && row.nextElementSibling;
        if (!submenu) return;
        const open = submenu.hasAttribute('hidden');
        if (open) submenu.removeAttribute('hidden');
        else submenu.setAttribute('hidden', '');
        drawerToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        drawerToggle.classList.toggle('is-open', open);
        return;
      }

      // Click outside an open desktop dropdown closes it
      if (!e.target.closest('[data-mim-menu-item]')) {
        Mim.utils.qsa('[data-mim-menu-item].is-open').forEach((el) => this._closeDesk(el));
      }
    },

    _onKeydown(e) {
      if (e.key !== 'Escape') return;
      const open = Mim.utils.qsa('[data-mim-menu-item].is-open');
      if (!open.length) return;
      open.forEach((el) => this._closeDesk(el));
      const focused = open[0].querySelector('[data-mim-submenu-toggle]');
      if (focused) focused.focus();
    },

    _closeDesk(item) {
      item.classList.remove('is-open');
      const toggle = item.querySelector('[data-mim-submenu-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    },
  };

  Mim.ready(() => Mim.register('menu', Menu));
})();

/* Reveal on scroll. Two effects share one observer:
     [data-mim-reveal]  containers fade/lift in (elements entering together
                        are staggered);
     [data-mim-print]   images (snippets/image.liquid) are built back layer by
                        layer — the theme's print reveal, set in Theme
                        settings → Animations → Image reveal.
   Progressive: content is never hidden without JS, elements already on screen
   at load are left alone (no flash, and the LCP image never carries
   data-mim-print at all), and nothing animates under reduced motion, with the
   theme's animations setting off, or inside the theme editor. */

(function () {
  'use strict';
  if (!window.Mim) return;
  const Mim = window.Mim;

  const STAGGER_MS = 80;
  const SELECTOR = '[data-mim-reveal], [data-mim-print]';

  const Reveal = {
    init() {
      if (!('IntersectionObserver' in window)) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (document.body.dataset.mimAnimations === 'false') return;
      if (window.Shopify && window.Shopify.designMode) return;

      this.io = new IntersectionObserver((entries) => this.onIntersect(entries), {
        rootMargin: '0px 0px -8% 0px',
      });
      this.scan(document);
    },

    // `print` images and `reveal` containers keep separate state attributes, so
    // an image inside a revealing container runs its own build.
    stateKey(el) {
      return el.hasAttribute('data-mim-print') ? 'mimPrintState' : 'mimRevealState';
    },

    scan(root) {
      const viewport = window.innerHeight;
      Mim.utils.qsa(SELECTOR, root).forEach((el) => {
        const key = this.stateKey(el);
        if (el.dataset[key]) return;
        if (el.getBoundingClientRect().top < viewport) return; // already visible
        el.dataset[key] = 'pending';
        this.io.observe(el);
      });
    },

    onIntersect(entries) {
      let i = 0;
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        el.style.setProperty('--mim-reveal-delay', `${i++ * STAGGER_MS}ms`);
        el.dataset[this.stateKey(el)] = 'in';
        this.io.unobserve(el);
        // Drop the print cover (and its will-change) once it has left the frame.
        if (el.hasAttribute('data-mim-print')) {
          el.addEventListener('animationend', (e) => {
            if (e.animationName === 'mim-print-build') el.dataset.mimPrintState = 'done';
          });
        }
      });
    },
  };

  Mim.ready(() => Mim.register('reveal', Reveal));
})();

/* Drag — pointer dragging for one-slide-per-view tracks, shared by the
   slideshow and the product gallery. In the global bundle; no init.

   Mim.modules.drag.attach(track, {
     mode: 'scroll' | 'swipe',  // scroll (default): `track` is a native scroll-snap
                                 // row — the mouse drags it (touch/trackpad already
                                 // swipe it natively), and on release it glides on
                                 // from the release speed to the chosen slide.
                                 // swipe: slides don't move (e.g. a fade gallery) —
                                 // mouse or touch, a horizontal swipe calls onSwipe.
     index: () => n,            // current slide
     count: () => n,            // number of slides
     onSettle: (i) => {},       // scroll: target slide chosen (mark it active)
     onSwipe: (dir) => {},      // swipe: -1 previous / +1 next
     onStart / onEnd: () => {}, // e.g. hold autoplay while dragging
     snapTo: (left, v) => px,   // scroll mode, multi-item rows (carousels): instead
                                 // of index/count, return where to land from the
                                 // release position and scroll speed (px/ms).
   })

   Release rule: a flick (> 0.4 px/ms) or a drag past 15% of the width moves
   one slide, otherwise it springs back; no wrap-around. A drag never clicks
   the link/button it ends on. Drags starting on 3D models, video, iframes or
   [data-mim-no-drag] are left alone (they have their own pointer handling).
   While dragging / gliding the track carries .is-dragging / .is-gliding
   (components/_drag.scss turns scroll-snap off so nothing jumps). */

(function () {
  'use strict';
  if (!window.Mim) return;
  const Mim = window.Mim;
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const IGNORE = 'model-viewer, video, iframe, input, select, textarea, .shopify-model-viewer-ui, [data-mim-no-drag]';

  // Ease-out scroll from the current position to `to`, starting at `velocity`
  // (scroll px/ms). Snapping stays off (.is-gliding) until it lands exactly.
  function glide(track, to, velocity, maxDuration = 650) {
    cancelAnimationFrame(track._mimGlide);
    track.classList.add('is-gliding');
    const from = track.scrollLeft;
    const dist = to - from;
    if (reduceMotion() || Math.abs(dist) < 1) {
      track.scrollLeft = to;
      track.classList.remove('is-gliding');
      return;
    }
    // easeOutCubic starts at speed 3·dist/duration → match the release speed
    // when it points the right way; keep it between 220ms and maxDuration.
    const towards = velocity * dist > 0 ? Math.abs(velocity) : 0;
    const duration = Math.min(maxDuration, Math.max(220, towards ? (3 * Math.abs(dist)) / towards : 450));
    const t0 = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      track.scrollLeft = from + dist * (1 - Math.pow(1 - p, 3));
      if (p < 1) track._mimGlide = requestAnimationFrame(step);
      else track.classList.remove('is-gliding');
    };
    track._mimGlide = requestAnimationFrame(step);
  }

  function stop(track) {
    cancelAnimationFrame(track._mimGlide);
    track.classList.remove('is-gliding');
  }

  function attach(track, o) {
    if (!track || track.dataset.mimDrag) return;
    const scroll = o.mode !== 'swipe';
    track.dataset.mimDrag = scroll ? 'scroll' : 'swipe';

    let start = null;
    let moved = false;
    let samples = [];

    Mim.utils.on(track, 'dragstart', (e) => e.preventDefault());

    Mim.utils.on(track, 'pointerdown', (e) => {
      if (e.button !== 0 || e.target.closest(IGNORE)) return;
      // A track laid out flat (e.g. the product gallery's desktop grid) has
      // nothing to drag.
      if (scroll && track.scrollWidth <= track.clientWidth) return;
      // Touch already scrolls a scroll track natively.
      if (scroll && e.pointerType !== 'mouse') return;
      if (scroll) stop(track);
      start = { x: e.clientX, y: e.clientY, left: track.scrollLeft, index: o.index ? o.index() : 0, id: e.pointerId };
      samples = [{ x: e.clientX, t: performance.now() }];
      moved = false;
    });

    Mim.utils.on(track, 'pointermove', (e) => {
      if (!start || e.pointerId !== start.id) return;
      const dx = e.clientX - start.x;
      if (!moved) {
        if (Math.abs(dx) < 6) return;
        if (Math.abs(e.clientY - start.y) > Math.abs(dx)) { start = null; return; } // vertical: let the page scroll
        moved = true;
        try { track.setPointerCapture(e.pointerId); } catch (err) { /* pointer already gone */ }
        track.classList.add('is-dragging');
        if (o.onStart) o.onStart();
      }
      if (scroll) track.scrollLeft = start.left - dx;
      const now = performance.now();
      samples.push({ x: e.clientX, t: now });
      while (samples.length > 2 && now - samples[0].t > 100) samples.shift();
    });

    const end = (e) => {
      if (!start || e.pointerId !== start.id) return;
      const s = start;
      start = null;
      if (!moved) return;
      const dx = e.clientX - s.x;
      // Pointer speed over the last ~100ms, px/ms (+ = moving right).
      const first = samples[0];
      const last = samples[samples.length - 1];
      const v = last.t > first.t ? (last.x - first.x) / (last.t - first.t) : 0;
      if (scroll && o.snapTo) {
        // Free rows (carousels): coast on, land on a card; longer than slides.
        glide(track, o.snapTo(track.scrollLeft, -v), -v, 1000);
        track.classList.remove('is-dragging');
        if (o.onEnd) o.onEnd();
        return;
      }

      let dir = 0;
      if (Math.abs(v) > 0.4) dir = v < 0 ? 1 : -1;
      else if (Math.abs(dx) > track.clientWidth * 0.15) dir = dx < 0 ? 1 : -1;
      const target = Math.max(0, Math.min(o.count() - 1, s.index + dir));

      if (scroll) {
        // Glide first: it switches to .is-gliding while snapping is still off,
        // otherwise dropping .is-dragging would snap the track mid-way.
        glide(track, target * track.clientWidth, -v);
        track.classList.remove('is-dragging');
        if (o.onSettle) o.onSettle(target);
      } else {
        track.classList.remove('is-dragging');
        if (target !== s.index && o.onSwipe) o.onSwipe(target - s.index);
      }
      if (o.onEnd) o.onEnd();
    };
    Mim.utils.on(track, 'pointerup', end);
    Mim.utils.on(track, 'pointercancel', end);

    // A drag that ends over a link or button must not click it.
    Mim.utils.on(track, 'click', (e) => {
      if (!moved) return;
      e.preventDefault();
      e.stopPropagation();
      moved = false;
    }, true);
  }

  Mim.register('drag', { attach, glide, stop });
})();

/* Quick view — product cards' "choose options" button opens the product's
   gallery + buy form in a drawer, fetched with the Section Rendering API
   (`{product url}?section_id=quick-view`, plus `variant=` when a card swatch
   picked one). The product JS module is loaded on
   first use (its URL comes from the layout, window.MimAssets.product) and
   mounted on the fetched markup. The button is a link to the product page,
   so without JS (or on a fetch error) it simply navigates. Global bundle. */

(function () {
  'use strict';
  if (!window.Mim) return;
  const Mim = window.Mim;

  const QuickView = {
    cache: new Map(),

    init() {
      this.root = document.querySelector('[data-mim-drawer="quick-view"]');
      if (!this.root) return;
      this.body = this.root.querySelector('[data-mim-quick-view-body]');
      this.loading = this.body.innerHTML;

      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-mim-quick-view]');
        if (!trigger || e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        this.open(trigger);
      });

      // Warm the cache on hover / focus so the drawer usually opens filled.
      const warm = (e) => {
        const trigger = e.target.closest && e.target.closest('[data-mim-quick-view]');
        if (trigger) this.fetch(trigger.dataset.mimQuickView).catch(() => {});
      };
      document.addEventListener('pointerover', warm, { passive: true });
      document.addEventListener('focusin', warm);
    },

    fetch(url) {
      if (!this.cache.has(url)) {
        // The URL may carry ?variant= (a card swatch) — keep it.
        const target = new URL(url, window.location.origin);
        target.searchParams.set('section_id', 'quick-view');
        const req = fetch(target.pathname + target.search)
          .then((res) => {
            if (!res.ok) throw new Error(res.status);
            return res.text();
          })
          .catch((err) => {
            this.cache.delete(url);
            throw err;
          });
        this.cache.set(url, req);
      }
      return this.cache.get(url);
    },

    async open(trigger) {
      const url = trigger.dataset.mimQuickView;
      this.returnFocus = trigger;
      this.body.innerHTML = this.loading;
      Mim.modules.drawer.open('quick-view');

      try {
        const html = await this.fetch(url);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('[data-mim-quick-view-content]');
        if (!content) throw new Error('empty');
        this.body.replaceChildren(content);
        this.body.scrollTop = 0;
        await this.mountProduct(content);
        const title = content.querySelector('.mim-product__title-link');
        if (title) title.focus({ preventScroll: true });
      } catch (err) {
        window.location.href = trigger.href;
      }
    },

    // Load mim-product.js once, then mount it on the fetched product.
    mountProduct(content) {
      const product = Mim.modules.product;
      if (product) {
        product.mount(content);
        return Promise.resolve();
      }
      const src = window.MimAssets && window.MimAssets.product;
      if (!src) return Promise.resolve();
      if (!this.scriptLoad) {
        this.scriptLoad = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = src;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      // On load the module self-registers and mounts every [data-mim-product].
      return this.scriptLoad;
    },
  };

  Mim.ready(() => Mim.register('quickView', QuickView));
})();

/* Product card — colour swatches. Each swatch button carries its variant's id,
   image (media id) and formatted price; clicking one shows that image, rewrites
   the price, and points the card's links (and quick view) at the variant. The
   variant images are rendered hidden in the card, lazy, so hovering / focusing
   a swatch starts their download before the click. Delegated on document, so
   cards added later (facets, predictive search, recommendations) just work.
   Global bundle. */

(function () {
  'use strict';
  if (!window.Mim) return;
  const Mim = window.Mim;

  const ProductCard = {
    init() {
      document.addEventListener('click', (e) => {
        const swatch = e.target.closest && e.target.closest('[data-mim-card-swatch]');
        if (swatch) this.select(swatch);
      });

      const warm = (e) => {
        const swatch = e.target.closest && e.target.closest('[data-mim-card-swatch]');
        if (!swatch) return;
        const image = this.image(swatch.closest('[data-mim-card]'), swatch.dataset.media);
        if (image) image.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      };
      document.addEventListener('pointerover', warm, { passive: true });
      document.addEventListener('focusin', warm);
    },

    image(card, id) {
      return card && card.querySelector(`[data-mim-card-image="${id}"]`);
    },

    select(swatch) {
      const card = swatch.closest('[data-mim-card]');
      if (!card) return;

      card.querySelectorAll('[data-mim-card-swatch]').forEach((s) => {
        s.setAttribute('aria-pressed', String(s === swatch));
      });

      const media = swatch.dataset.media;
      if (this.image(card, media)) {
        card.querySelectorAll('[data-mim-card-image]').forEach((el) => {
          el.hidden = el.dataset.mimCardImage !== media;
        });
      }
      // The hover image belongs to the default photo — skip it once another
      // colour is showing.
      card.classList.toggle('is-swatched', media !== card.dataset.mimCardMain);

      this.price(card, swatch.dataset.price, swatch.dataset.compare);

      const variant = swatch.dataset.variant;
      if (!variant) return;
      card.querySelectorAll('[data-mim-card-link]').forEach((a) => {
        const url = new URL(a.getAttribute('href'), window.location.origin);
        url.searchParams.set('variant', variant);
        a.setAttribute('href', url.pathname + url.search);
      });
      card.querySelectorAll('[data-mim-quick-view]').forEach((a) => {
        const url = new URL(a.dataset.mimQuickView, window.location.origin);
        url.searchParams.set('variant', variant);
        a.dataset.mimQuickView = url.pathname + url.search;
      });
    },

    price(card, price, compare) {
      const el = card.querySelector('[data-mim-card-price]');
      if (!el || !price) return;
      if (compare) {
        const was = document.createElement('s');
        was.className = 'mim-product-card__price--was';
        was.textContent = compare;
        const now = document.createElement('span');
        now.className = 'mim-product-card__price--now';
        now.textContent = price;
        el.replaceChildren(was, now);
      } else {
        el.textContent = price;
      }
    },
  };

  Mim.ready(() => Mim.register('productCard', ProductCard));
})();

/* Locale menu — the compact country / language disclosures
   (snippets/localization-form, style 'compact'). They work as plain
   <details> without JS; this closes an open one on outside click or Escape
   (focus back on its toggle) and keeps only one open at a time. */

(function () {
  'use strict';
  if (!window.Mim) return;
  const Mim = window.Mim;

  const LocaleMenu = {
    init() {
      Mim.utils.on(document, 'click', (e) => {
        Mim.utils.qsa('[data-mim-locale-menu][open]').forEach((menu) => {
          if (!menu.contains(e.target)) menu.removeAttribute('open');
        });
      });

      Mim.utils.on(document, 'keydown', (e) => {
        if (e.key !== 'Escape') return;
        const menu = e.target.closest && e.target.closest('[data-mim-locale-menu][open]');
        if (!menu) return;
        menu.removeAttribute('open');
        menu.querySelector('summary').focus();
      });

      Mim.utils.on(document, 'toggle', (e) => {
        const menu = e.target;
        if (!menu.matches || !menu.matches('[data-mim-locale-menu]') || !menu.open) return;
        Mim.utils.qsa('[data-mim-locale-menu][open]').forEach((other) => {
          if (other !== menu) other.removeAttribute('open');
        });
      }, true);
    },
  };

  Mim.ready(() => Mim.register('localeMenu', LocaleMenu));
})();

/* Cart — site-wide add-to-cart interception + AJAX qty/remove via the
   Section Rendering API + header count sync. Part of the global bundle.

   Paid add-ons: a "Custom option" can price itself through a companion
   product (Shopify can't charge for a line item property). The product form
   then carries a hidden [data-mim-addon] marker and the add goes out as one
   request with two items — the product and its companion, linked by the
   hidden properties `_addon_for` / `_addon_key` — and a later quantity change
   or removal of the product line is applied to the companion as well. */

(function () {
  'use strict';
  if (!window.Mim) return;
  const Mim = window.Mim;

  const Cart = {
    SECTION: 'cart-drawer',

    init() {
      // 'drawer' (default) = AJAX add + open drawer; 'page' = native submit → /cart.
      this.mode = (document.body && document.body.dataset.mimCartMode) || 'drawer';

      // Add-to-cart: intercept product form submits (works site-wide,
      // so the per-page Product module doesn't need to know about the cart).
      // In page mode we let the form submit natively so Shopify navigates to /cart.
      // Product forms are `novalidate` (errors show inline), so required
      // custom options and quantity rules are checked here in both modes.
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[action$="/cart/add"]');
        if (!form) return;
        const invalid = Array.from(form.elements).find((el) => el.willValidate && !el.checkValidity());
        if (invalid) {
          e.preventDefault();
          this._reportInvalid(invalid);
          return;
        }
        // Page mode submits natively — except with a paid add-on, which only
        // an AJAX add can send as one request; the cart page follows.
        if (this.mode === 'page' && !this._addons(form).length) return;
        e.preventDefault();
        this.addItem(form);
      });

      // Quantity +/- and remove, delegated so re-rendered nodes keep working.
      document.addEventListener('click', (e) => {
        const step = e.target.closest('[data-mim-qty-change]');
        if (step) {
          const wrap = step.closest('[data-mim-qty]');
          const input = wrap && wrap.querySelector('[data-mim-qty-input]');
          if (input) {
            const next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.mimQtyChange, 10));
            this.changeQty(input.dataset.mimLineKey, next);
          }
          return;
        }
        const remove = e.target.closest('[data-mim-cart-remove]');
        if (remove) {
          e.preventDefault();
          this.changeQty(remove.dataset.mimLineKey, 0);
        }
      });

      // Order note: saved to the cart while typing (debounced).
      const saveNote = Mim.utils.debounce((value) => {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: value }),
        }).catch(() => {});
      }, 400);
      document.addEventListener('input', (e) => {
        const note = e.target.closest('[data-mim-cart-note]');
        if (note) saveNote(note.value);
      });

      // Typed quantity.
      document.addEventListener('change', (e) => {
        const input = e.target.closest('[data-mim-qty-input]');
        if (!input) return;
        const qty = Math.max(0, parseInt(input.value, 10) || 0);
        this.changeQty(input.dataset.mimLineKey, qty);
      });
    },

    // Sections to re-render: always the drawer, plus the cart page if present.
    _sectionList() {
      const ids = [this.SECTION];
      const page = document.querySelector('[data-mim-cart-section-id]');
      if (page) ids.push(page.dataset.mimCartSectionId);
      return ids.join(',');
    },

    async addItem(form) {
      const addons = this._addons(form);
      const sections = { sections: this._sectionList(), sections_url: window.location.pathname };
      let request;
      if (addons.length) {
        request = {
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(Object.assign({ items: this._itemsWithAddons(form, addons) }, sections)),
        };
      } else {
        const body = new FormData(form);
        body.append('sections', sections.sections);
        body.append('sections_url', sections.sections_url);
        request = { headers: { Accept: 'application/json' }, body };
      }
      try {
        const res = await fetch('/cart/add.js', Object.assign({ method: 'POST' }, request));
        const data = await res.json();
        if (!res.ok) {
          this._error(form, data && data.description);
          return;
        }
        if (this.mode === 'page') {
          // Native submit would have landed on the cart page; do the same.
          window.location.href = form.getAttribute('action').replace(/\/add$/, '');
          return;
        }
        if (data.sections) this._renderSections(data.sections);
        await this._syncCount();
        const drawer = Mim.modules.drawer;
        if (drawer) drawer.open('cart');
      } catch (err) {
        this._error(form);
      }
    },

    async changeQty(key, quantity) {
      if (!key) return;
      const linked = await this._linkedAddons(key);
      if (linked.length) {
        const done = await this._changeLinked(key, quantity, linked);
        if (done) return;
      }
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: this._sectionList(), sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (res.ok) this._cart = data;
        if (data.sections) this._renderSections(data.sections);
        this._setCount(data.item_count);
      } catch (err) {
        /* keep current DOM on failure */
      }
    },

    // ── Paid add-ons (companion products) ──────────────────────────────────

    // The markers of this form's priced options, each resolved to "add the
    // companion now?": always, or only when the field is filled in (text) /
    // a value other than the first, free one is picked (buttons, dropdown).
    _addons(form) {
      // getAttribute, not .id: the variant <input name="id"> shadows it.
      const formId = form.getAttribute('id');
      if (!formId) return [];
      return Mim.utils.qsa(`[data-mim-addon][data-mim-addon-form="${formId}"]`)
        .filter((marker) => {
          if (marker.dataset.mimAddonWhen !== 'filled') return true;
          const field = form.elements.namedItem(marker.dataset.mimAddonField);
          const value = field && typeof field.value === 'string' ? field.value.trim() : '';
          if (value === '') return false;
          const base = marker.dataset.mimAddonBase;
          return typeof base !== 'string' || value !== base.trim();
        })
        .map((marker) => parseInt(marker.dataset.mimAddon, 10))
        .filter((id) => id > 0);
    },

    // The form as `/cart/add.js` items: the product (with its properties and
    // selling plan) plus one companion per add-on, in the same quantity. Both
    // sides carry `_addon_key`, so the pair can be found again in the cart.
    _itemsWithAddons(form, addons) {
      const data = new FormData(form);
      const key = Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
      const main = {
        id: parseInt(data.get('id'), 10),
        quantity: Math.max(1, parseInt(data.get('quantity'), 10) || 1),
        properties: { _addon_key: key },
      };
      if (data.get('selling_plan')) main.selling_plan = parseInt(data.get('selling_plan'), 10);
      data.forEach((value, name) => {
        const m = typeof value === 'string' && name.match(/^properties\[(.+)\]$/);
        if (m) main.properties[m[1]] = value;
      });
      const companions = addons.map((id) => ({
        id,
        quantity: main.quantity,
        properties: { _addon_for: String(main.id), _addon_key: key },
      }));
      return [main].concat(companions);
    },

    // Keys of the companion lines tied to a product line (none for an
    // ordinary line, or for a companion itself). Read off the cart's items —
    // fetched once, then kept from each response — never off the markup.
    async _linkedAddons(key) {
      const cart = await this._cartState();
      if (!cart) return [];
      const line = cart.items.find((item) => item.key === key);
      const props = line && line.properties;
      if (!props || props._addon_for || !props._addon_key) return [];
      return cart.items
        .filter((item) => item !== line && item.properties && item.properties._addon_for && item.properties._addon_key === props._addon_key)
        .map((item) => item.key);
    },

    // One request for the product line and its companions, so the cart never
    // shows a half-updated pair. False when it fails (a stale key, say) — the
    // caller then changes the product line alone.
    async _changeLinked(key, quantity, linked) {
      const updates = { [key]: quantity };
      linked.forEach((k) => { updates[k] = quantity; });
      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates, sections: this._sectionList(), sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (!res.ok) {
          this._cart = null;
          return false;
        }
        this._cart = data;
        if (data.sections) this._renderSections(data.sections);
        this._setCount(data.item_count);
        return true;
      } catch (err) {
        this._cart = null;
        return false;
      }
    },

    // The cart as `/cart.js` gives it, kept from the last response.
    async _cartState() {
      if (this._cart) return this._cart;
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        this._cart = await res.json();
      } catch (err) {
        this._cart = null;
      }
      return this._cart;
    },

    _renderSections(sections) {
      this._render(sections[this.SECTION]);
      this._renderPage(sections);
    },

    async _syncCount() {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const data = await res.json();
        this._cart = data;
        this._setCount(data.item_count);
      } catch (err) {
        /* noop */
      }
    },

    _render(html) {
      if (!html) return;
      const drawer = document.querySelector('[data-mim-drawer="cart"]');
      if (!drawer) return;
      const next = new DOMParser().parseFromString(html, 'text/html');
      const newBody = next.querySelector('[data-mim-cart-contents]');
      const liveBody = drawer.querySelector('[data-mim-cart-contents]');
      if (newBody && liveBody) liveBody.replaceWith(newBody);

      const newFooter = next.querySelector('[data-mim-cart-footer]');
      const liveFooter = drawer.querySelector('[data-mim-cart-footer]');
      if (newFooter && liveFooter) liveFooter.replaceWith(newFooter);
      else if (newFooter && !liveFooter) (newBody || liveBody).after(newFooter);
      else if (!newFooter && liveFooter) liveFooter.remove();
    },

    // Re-render the full cart page (its [data-mim-cart-main] block), if present.
    _renderPage(sections) {
      const pageEl = document.querySelector('[data-mim-cart-section-id]');
      if (!pageEl) return;
      const html = sections[pageEl.dataset.mimCartSectionId];
      if (!html) return;
      const next = new DOMParser().parseFromString(html, 'text/html');
      const newMain = next.querySelector('[data-mim-cart-main]');
      const liveMain = pageEl.querySelector('[data-mim-cart-main]');
      if (newMain && liveMain) liveMain.replaceWith(newMain);
    },

    _setCount(count) {
      if (typeof count !== 'number') return;
      Mim.utils.qsa('[data-mim-cart-count]').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('is-empty', count === 0);
      });
    },

    _error(form, message) {
      const target = form && form.querySelector('[data-mim-cart-error]');
      if (target) target.textContent = message || 'Could not add to cart.';
    },

    // An invalid field: its message under the field when the markup offers a
    // place for it ([data-mim-field-error], cleared again as the shopper
    // types), the browser's bubble otherwise.
    _reportInvalid(el) {
      const target = el.id && document.querySelector(`[data-mim-field-error="${el.id}"]`);
      if (!target) {
        el.reportValidity();
        return;
      }
      target.textContent = el.dataset.mimRequiredMessage && el.validity.valueMissing
        ? el.dataset.mimRequiredMessage
        : el.validationMessage;
      target.hidden = false;
      el.setAttribute('aria-invalid', 'true');
      el.focus();
      const clear = () => {
        if (!el.checkValidity()) return;
        target.hidden = true;
        target.textContent = '';
        el.removeAttribute('aria-invalid');
        el.removeEventListener('input', clear);
      };
      el.addEventListener('input', clear);
    },
  };

  Mim.ready(() => Mim.register('cart', Cart));
})();

/* Search — predictive results in the search drawer (Section Rendering API).
   Part of the global bundle (the drawer lives in the layout). */

(function () {
  'use strict';
  if (!window.Mim) return;
  const Mim = window.Mim;

  const Search = {
    SECTION: 'predictive-search',
    MIN: 2,

    init() {
      this.input = document.querySelector('[data-mim-search-input]');
      this.results = document.querySelector('[data-mim-predictive-results]');
      if (!this.input || !this.results) return;

      const run = Mim.utils.debounce(() => this.query(this.input.value.trim()), 200);
      Mim.utils.on(this.input, 'input', run);

      // Focus the field when the search drawer is opened.
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-mim-drawer-open="search"]')) {
          setTimeout(() => this.input.focus(), 60);
        }
      });
    },

    async query(term) {
      if (term.length < this.MIN) {
        this.results.innerHTML = '';
        return;
      }
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,query,page,article',
        'resources[limit]': '6',
        section_id: this.SECTION,
      });
      try {
        const res = await fetch(`/search/suggest?${params}`);
        this.results.innerHTML = await res.text();
      } catch (err) {
        /* leave previous results on failure */
      }
    },
  };

  Mim.ready(() => Mim.register('search', Search));
})();

/* Announcement bar — auto-rotate + prev/next when more than one message.
   Part of the global bundle (announcement bar lives in the header group). */

(function () {
  'use strict';
  if (!window.Mim) return;
  const Mim = window.Mim;

  const Announcement = {
    init() {
      const bar = document.querySelector('[data-mim-announcement]');
      if (!bar) return;
      this.items = Mim.utils.qsa('[data-mim-announcement-item]', bar);
      if (this.items.length < 2) return;

      this.index = 0;
      const prev = bar.querySelector('[data-mim-announcement-prev]');
      const next = bar.querySelector('[data-mim-announcement-next]');
      if (prev) Mim.utils.on(prev, 'click', () => { this.go(-1); this.stop(); });
      if (next) Mim.utils.on(next, 'click', () => { this.go(1); this.stop(); });

      const rotate = bar.dataset.mimAnnouncementRotate;
      if (rotate) {
        const ms = (parseInt(rotate, 10) || 5) * 1000;
        this.timer = setInterval(() => this.go(1), ms);
        Mim.utils.on(bar, 'mouseenter', () => this.stop());
      }
    },

    // The stylesheet animates `is-entering` / `is-leaving` according to the
    // section's Transition setting; the direction class tells a slide which
    // way to travel. Both are cleared when the animation ends — or after a
    // beat, for the "None" setting and reduced motion, where nothing animates.
    go(dir) {
      const from = this.items[this.index];
      this.index = (this.index + dir + this.items.length) % this.items.length;
      const to = this.items[this.index];
      const way = dir > 0 ? 'is-dir-next' : 'is-dir-prev';
      const settle = (el, state) => {
        const done = () => { el.classList.remove(state, 'is-dir-next', 'is-dir-prev'); clearTimeout(timer); };
        const timer = setTimeout(done, 1000);
        el.addEventListener('animationend', done, { once: true });
      };

      this.items.forEach((el) => el.classList.remove('is-entering', 'is-leaving', 'is-dir-next', 'is-dir-prev'));
      from.classList.remove('is-active');
      from.classList.add('is-leaving', way);
      to.classList.add('is-active', 'is-entering', way);
      settle(from, 'is-leaving');
      settle(to, 'is-entering');
    },

    stop() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    },
  };

  Mim.ready(() => Mim.register('announcement', Announcement));
})();

/* Header — scroll state (is-scrolled: turns a transparent header solid) and
   --mim-header-height on <html> (the header's live height, used by sticky
   columns and offsets). Part of the global bundle. */

(function () {
  'use strict';
  if (!window.Mim) return;
  const Mim = window.Mim;

  const Header = {
    init() {
      const header = document.querySelector('[data-mim-header]');
      if (!header) return;
      this.el = header;
      this._onScroll = Mim.utils.debounce(() => {
        header.classList.toggle('is-scrolled', window.scrollY > 8);
      }, 16);
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._onScroll();

      const setHeight = () => {
        document.documentElement.style.setProperty('--mim-header-height', `${Math.round(header.offsetHeight)}px`);
      };
      setHeight();
      if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(header);
    },
  };

  Mim.ready(() => Mim.register('header', Header));
})();
